'use client'

import { CheckCircle2, Loader2 } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import Link from 'next/link'
import React, { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { FormError } from '@/components/forms/FormError'
import { FormItem } from '@/components/forms/FormItem'
import { Message } from '@/components/Message'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { VEZELKAART_SUBMIT_LABEL } from '@/utilities/vezelkaarten'

type FormData = {
  firstName: string
  email: string
  newsletterOptIn: boolean
}

export function VezelkaartRequestForm() {
  const posthog = usePostHog()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      firstName: '',
      email: '',
      newsletterOptIn: false,
    },
  })

  const onSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch('/api/vezelkaarten/request', {
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        })

        const json = await response.json().catch(() => null)

        if (!response.ok) {
          setError(json?.message || 'Versturen is niet gelukt. Probeer het opnieuw.')
          return
        }

        posthog?.capture('vezelkaart_request', {
          newsletter_opt_in: data.newsletterOptIn,
        })
        posthog?.setPersonProperties({ email: data.email, first_name: data.firstName })

        reset()
        setSubmitted(true)
      } catch (_) {
        setError('Versturen is niet gelukt. Controleer je verbinding en probeer het opnieuw.')
      } finally {
        setLoading(false)
      }
    },
    [posthog, reset],
  )

  if (submitted) {
    return (
      <div
        aria-live="polite"
        className="rounded-xl border border-[#085e41]/20 bg-[#085e41]/5 p-6 text-center"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#085e41]" />
        <h3 className="mt-4 text-lg font-bold text-foreground">Bedankt voor je aanvraag!</h3>
        <p className="mt-2 leading-7 text-muted-foreground">
          De vezelkaarten zijn naar je e-mailadres verzonden. Controleer ook je map met ongewenste
          e-mail wanneer je de e-mail niet direct ziet.
        </p>
      </div>
    )
  }

  return (
    <form className="space-y-5" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Message error={error} />

      <FormItem>
        <Label htmlFor="vezelkaart-email" className="text-sm font-medium text-foreground">
          E-mailadres
        </Label>
        <Input
          id="vezelkaart-email"
          {...register('email', {
            required: 'E-mailadres is verplicht.',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Vul een geldig e-mailadres in.',
            },
          })}
          autoComplete="email"
          className="h-11 rounded-lg border-border/70 bg-background transition-colors focus:border-primary/50 focus:ring-primary/20"
          placeholder="naam@voorbeeld.nl"
          type="email"
        />
        {errors.email && <FormError message={errors.email.message} />}
      </FormItem>

      <FormItem>
        <Label htmlFor="vezelkaart-first-name" className="text-sm font-medium text-foreground">
          Voornaam
        </Label>
        <Input
          id="vezelkaart-first-name"
          {...register('firstName', { required: 'Voornaam is verplicht.' })}
          autoComplete="given-name"
          className="h-11 rounded-lg border-border/70 bg-background transition-colors focus:border-primary/50 focus:ring-primary/20"
          placeholder="Je voornaam"
        />
        {errors.firstName && <FormError message={errors.firstName.message} />}
      </FormItem>

      <Controller
        control={control}
        name="newsletterOptIn"
        render={({ field }) => (
          <label
            htmlFor="vezelkaart-newsletter"
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/70 bg-background p-4 text-sm leading-6 text-muted-foreground transition-colors hover:border-primary/30"
          >
            <Checkbox
              id="vezelkaart-newsletter"
              checked={field.value}
              className="mt-1"
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
            <span>
              Ja, ik ontvang graag aanvullende tips en nieuws over gezonde en glutenvrije voeding.
            </span>
          </label>
        )}
      />

      <Button
        className="h-12 w-full rounded-lg bg-[#085e41] font-semibold text-white shadow-lg shadow-[#085e41]/25 transition-all hover:bg-[#2D5A3D] hover:shadow-xl hover:shadow-[#085e41]/30"
        disabled={loading}
        type="submit"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? 'Versturen...' : VEZELKAART_SUBMIT_LABEL}
      </Button>

      <p className="text-xs leading-5 text-muted-foreground">
        We gebruiken je gegevens alleen om de aangevraagde vezelkaarten toe te sturen. Bekijk het{' '}
        <Link className="underline underline-offset-2 hover:text-foreground" href="/privacybeleid">
          privacybeleid
        </Link>{' '}
        voor meer informatie.
      </p>
    </form>
  )
}
